import {
  BehaviorSubject,
  combineLatest,
  filter,
  from,
  of,
  switchMap,
} from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import { profile$ } from "@/lib/profile";
import type {
  FamilySettings,
  MaxBackdateDays,
  Profile,
  ProfileData,
  WeekStartDay,
} from "@/types/domain";

interface FamilyState {
  members: Profile[];
  settings: FamilySettings | null;
  loading: boolean;
}

const state$ = new BehaviorSubject<FamilyState>({
  members: [],
  settings: null,
  loading: false,
});

const authedFamily$ = profile$.pipe(
  filter((s) => !s.loading && s.profile !== null),
);

const profiles$ = authedFamily$.pipe(
  switchMap((s) =>
    from(getDb()).pipe(
      switchMap(
        (db) =>
          db.week_allowance.find({
            selector: {
              family_id: s.profile!.data.family_id,
              type: "profile",
            },
          }).$,
      ),
    ),
  ),
  switchMap((docs) =>
    of(
      docs.map((d) => {
        const r = d.toJSON() as RecordDoc;
        return {
          id: r.user_id as string,
          data: r.data as unknown as ProfileData,
        };
      }),
    ),
  ),
);

const settings$ = authedFamily$.pipe(
  switchMap((s) =>
    from(getDb()).pipe(
      switchMap(
        (db) =>
          db.week_allowance.findOne({
            selector: {
              family_id: s.profile!.data.family_id,
              type: "family_settings",
            },
          }).$,
      ),
    ),
  ),
  switchMap((doc) =>
    of(
      doc
        ? ((doc.toJSON() as RecordDoc).data as unknown as FamilySettings)
        : null,
    ),
  ),
);

authedFamily$.subscribe(() => {
  state$.next({ ...state$.getValue(), loading: true });
});

combineLatest([profiles$, settings$]).subscribe(([members, settings]) => {
  state$.next({ members, settings, loading: false });
});

import { DEFAULT_WEEK_START_DAY } from "@/lib/week";

const DEFAULT_MAX_BACKDATE_DAYS: MaxBackdateDays = 1;

async function patchSettings(
  familyId: string,
  patch: Partial<FamilySettings>,
): Promise<void> {
  const db = await getDb();
  const doc = await db.week_allowance
    .findOne({
      selector: { family_id: familyId, type: "family_settings" },
    })
    .exec();
  if (!doc) return;
  const json = doc.toJSON() as RecordDoc;
  const merged: FamilySettings = {
    ...(json.data as unknown as FamilySettings),
    ...patch,
  };
  await doc.patch({
    data: merged as unknown as Record<string, unknown>,
    updated_at: new Date().toISOString(),
  });
}

function currentFamilyId(): string | null {
  const current = state$.getValue();
  return current.members.find((m) => m.data.family_id)?.data.family_id ?? null;
}

export const familyService = {
  state$: state$.asObservable(),

  get snapshot(): FamilyState {
    return state$.getValue();
  },

  children(): Profile[] {
    return state$.getValue().members.filter((m) => m.data.role === "child");
  },

  /** Current family week-start-day, or default (Mon) if not yet loaded. */
  weekStartDay() {
    return state$.getValue().settings?.week_start_day ?? DEFAULT_WEEK_START_DAY;
  },

  /** Days back a child is allowed to log on, including today (0..N). */
  maxBackdateDays(): MaxBackdateDays {
    return (
      state$.getValue().settings?.max_backdate_days ?? DEFAULT_MAX_BACKDATE_DAYS
    );
  },

  async updateWeekStartDay(weekStartDay: WeekStartDay): Promise<void> {
    const familyId = currentFamilyId();
    if (!familyId) return;
    await patchSettings(familyId, { week_start_day: weekStartDay });
  },

  async updateMaxBackdateDays(value: MaxBackdateDays): Promise<void> {
    const familyId = currentFamilyId();
    if (!familyId) return;
    await patchSettings(familyId, { max_backdate_days: value });
  },
};
